import { TutorData } from "@/data/mentors";
import Aos from "aos";
import { Clock } from "lucide-react";
import { useEffect } from "react";
import { BiCheckShield } from "react-icons/bi";
import { FaMoneyCheck } from "react-icons/fa6";
import { Link } from "react-router-dom";

interface Result {
  results: TutorData[];
}

export default function Content({ results }: Result) {
  useEffect(() => {
    Aos.init({ duration: 1000, once: true });
  }, []);

  const listBenefits = [
    {
      title: "Certified Tutors",
      desc: "Every tutor undergoes a rigorous selection and certification process, ensuring the expertise and professionalism you can rely on throughout your learning journey.",
      icon: <BiCheckShield size={24} className="text-[#B70100]" />,
    },
    {
      title: "Flexible Hours",
      desc: "Book lessons at a time that works for you, with flexible scheduling designed to accommodate learners across different time zones.",
      icon: <Clock size={24} className="text-[#B70100]" />,
    },
    {
      title: "Satisfaction Guaranteed",
      desc: "If your first trial lesson doesn't meet your expectations, you'll receive a full refund. We want you to find a learning experience that feels right for you.",
      icon: <FaMoneyCheck size={24} className="text-[#B70100]" />,
    },
  ];

  return (
    <>
      <div className="space-y-6 pt-16 sm:pt-24 md:pt-24 sm:-mt-12 lg:-mt-16 -mt-4 mb-10">
        <div data-aos="fade-up" className="flex flex-col gap-2">
          <h3 className="text-2xl lg:text-3xl font-semibold text-black">
            Learn from the Best
          </h3>
          <span className="text-gray-700 text-lg lg:text-xl font-light">
            Showing professional tutors
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {results.map((item, index) => (
            <div
              key={item.slug}
              className="bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="w-full aspect-[4/3] overflow-hidden">
                <img
                  src={item.imageSrc || "/placeholder.png"}
                  alt={item.title}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="p-5 flex flex-col gap-3 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-sm font-semibold text-red-500 mt-0.5">
                      {item.subtitle}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-yellow-400 text-lg">★</span>
                    <span className="bg-red-100 text-gray-800 text-sm font-semibold px-3 py-1 rounded-full">
                      {item.rating}
                    </span>
                  </div>
                </div>

                <p className="text-gray-500 text-sm leading-relaxed">
                  {item.desc}
                </p>

                <div className="flex flex-wrap gap-2 mt-auto pt-1">
                  {item.tags?.map((tag, i) => (
                    <span
                      key={i}
                      className="bg-red-100 text-gray-600 text-xs font-medium px-4 py-1.5 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/Future/Mentor/${item.slug}`}
                  className="mt-3 w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-semibold text-sm py-3.5 rounded-2xl transition-colors duration-200 text-center"
                >
                  View Profile
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center mb-10">
        <button className="text-center w-full md:w-auto bg-white text-[#FF0000] font-medium py-3 px-6 rounded-lg shadow-md transition-transform duration-300 transform hover:scale-105 ">
          Load More Tutors
        </button>
      </div>

      <div
        className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-6"
        data-aos="fade-up"
      >
        {listBenefits.map((item, index) => (
          <div
            key={index}
            className="flex w-full mb-4 rounded-lg items-start gap-4"
          >
            <div className="bg-white rounded-lg h-full flex flex-col items-center justify-center gap-2 py-8 px-10 w-full">
              <div className="bg-[#FFE2DD] rounded-full w-16 h-16 flex items-center justify-center">
                {item.icon}
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center">
                {item.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base text-wrap w-full text-center">
                {item.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
